/**
 * Sync Order availability-conflict modal + cart edit-weight flow.
 * Presentation only — WMS/backend remain the source of truth for final sync.
 */
(function (win) {
  'use strict';

  var FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** @param {unknown} value */
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** @param {unknown} raw */
  function normalizeWeightGrams(raw) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return Number(raw.toFixed(3));
    }
    var s = String(raw == null ? '' : raw).trim();
    if (!s) return null;
    var m = s.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!m) return null;
    var n = Number(m[0]);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Number(n.toFixed(3));
  }

  /** @param {number|null|undefined} grams */
  function formatConflictWeight(grams) {
    if (grams == null || !Number.isFinite(grams)) return '-';
    return Number(grams).toFixed(3) + ' g';
  }

  /** @param {string} raw */
  function splitCsv(raw) {
    return String(raw || '')
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  /** @param {Record<string, unknown>|null|undefined} item */
  function itemSku(item) {
    if (!item) return '';
    var direct = item.sku != null ? String(item.sku).trim() : '';
    if (direct) return direct;
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    return props.SKU != null ? String(props.SKU).trim() : '';
  }

  /** @param {Record<string, unknown>|null|undefined} item */
  function itemImage(item) {
    if (!item) return '';
    if (typeof item.image === 'string') return item.image;
    if (item.image && typeof item.image === 'object' && 'src' in item.image) {
      return String(/** @type {{src?: string}} */ (item.image).src || '');
    }
    var fi = item.featured_image;
    if (typeof fi === 'string') return fi;
    if (fi && typeof fi === 'object' && 'url' in fi) {
      return String(/** @type {{url?: string}} */ (fi).url || '');
    }
    return '';
  }

  /** @param {Record<string, unknown>|null|undefined} item */
  function itemTitle(item) {
    if (!item) return '';
    return String(item.product_title || item.title || '').trim();
  }

  /** @param {Record<string, unknown>|null|undefined} data */
  function getAvailabilityErrorCode(data) {
    if (!data || typeof data !== 'object') return '';
    return String(data.errorCode || data.code || '')
      .trim()
      .toUpperCase();
  }

  /** @param {string} code */
  function isAvailabilityConflictCode(code) {
    var normalized = String(code || '')
      .trim()
      .toUpperCase();
    return normalized === 'INSUFFICIENT_AVAILABILITY' || normalized === 'ALLOCATION_CONFLICT';
  }

  /**
   * Extract structured unavailable items from cart-sync error payload.
   * @param {Record<string, unknown>|null|undefined} data
   * @returns {Array<Record<string, unknown>>|null}
   */
  function extractAvailabilityConflicts(data) {
    if (!data || typeof data !== 'object') return null;
    var code = getAvailabilityErrorCode(data);
    if (!isAvailabilityConflictCode(code)) return null;

    var details = Array.isArray(data.details) ? data.details : [];
    /** @type {Array<Record<string, unknown>>} */
    var items = [];

    details.forEach(function (detail) {
      if (!detail || typeof detail !== 'object') return;
      var d = /** @type {Record<string, unknown>} */ (detail);
      if (Array.isArray(d.items)) {
        d.items.forEach(function (entry) {
          if (entry && typeof entry === 'object') items.push(/** @type {Record<string, unknown>} */ (entry));
        });
        return;
      }
      if (Array.isArray(d.unavailableItems)) {
        d.unavailableItems.forEach(function (entry) {
          if (entry && typeof entry === 'object') items.push(/** @type {Record<string, unknown>} */ (entry));
        });
        return;
      }
      if (d.sku != null || d.weight != null) items.push(d);
    });

    if (!items.length && Array.isArray(data.unavailableItems)) {
      data.unavailableItems.forEach(function (entry) {
        if (entry && typeof entry === 'object') items.push(/** @type {Record<string, unknown>} */ (entry));
      });
    }

    if (!items.length) return null;

    return items.map(function (raw, index) {
      var weight = normalizeWeightGrams(raw.weight);
      var unit = raw.weightUnit != null ? String(raw.weightUnit) : raw.unit != null ? String(raw.unit) : 'g';
      var lineKeys = Array.isArray(raw.lineKeys)
        ? raw.lineKeys.map(function (k) {
            return String(k || '').trim();
          }).filter(Boolean)
        : [];
      if (!lineKeys.length && raw.lineKey != null && String(raw.lineKey).trim()) {
        lineKeys = [String(raw.lineKey).trim()];
      }
      return {
        id:
          raw.id != null
            ? String(raw.id)
            : [String(raw.sku || ''), weight != null ? weight.toFixed(3) : '', String(index)].join('|'),
        code: code,
        lineKey: lineKeys[0] || (raw.lineKey != null ? String(raw.lineKey) : ''),
        lineKeys: lineKeys,
        productId: raw.productId != null ? String(raw.productId) : raw.product_id != null ? String(raw.product_id) : '',
        variantId: raw.variantId != null ? raw.variantId : raw.variant_id != null ? raw.variant_id : null,
        sku: raw.sku != null ? String(raw.sku).trim() : '',
        productName: raw.productName != null ? String(raw.productName) : raw.title != null ? String(raw.title) : '',
        image: raw.image != null ? String(raw.image) : '',
        weight: weight,
        weightUnit: unit || 'g',
        requestedQuantity:
          raw.requestedQuantity != null
            ? Number(raw.requestedQuantity)
            : raw.requestedQty != null
              ? Number(raw.requestedQty)
              : null,
        availableQuantity:
          raw.availableQuantity != null
            ? Number(raw.availableQuantity)
            : raw.availableCount != null
              ? Number(raw.availableCount)
              : code === 'ALLOCATION_CONFLICT'
                ? 0
                : null,
      };
    });
  }

  /**
   * @param {Array<Record<string, unknown>>} conflicts
   * @param {Record<string, unknown>} cart
   */
  function enrichConflictsFromCart(conflicts, cart) {
    var items = Array.isArray(cart.items) ? cart.items : [];
    return conflicts.map(function (conflict) {
      var next = Object.assign({}, conflict);
      var match = null;
      if (next.lineKey) {
        match = items.find(function (item) {
          return item && String(item.key || '') === String(next.lineKey);
        });
      }
      if (!match && next.sku) {
        match = items.find(function (item) {
          if (!item) return false;
          if (String(itemSku(/** @type {Record<string, unknown>} */ (item))).toUpperCase() !== String(next.sku).toUpperCase()) {
            return false;
          }
          return lineHasWeight(/** @type {Record<string, unknown>} */ (item), next.weight);
        });
      }
      if (match) {
        var m = /** @type {Record<string, unknown>} */ (match);
        if (!next.productName) next.productName = itemTitle(m);
        if (!next.image) next.image = itemImage(m);
        if (!next.productId && m.product_id != null) next.productId = String(m.product_id);
        if (next.variantId == null && m.variant_id != null) next.variantId = m.variant_id;
        if (!next.lineKey && m.key != null) next.lineKey = String(m.key);
        if (!next.lineKeys.length && next.lineKey) next.lineKeys = [next.lineKey];
        if (!next.sku) next.sku = itemSku(m);
      }
      return next;
    });
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {number|null|undefined} weightGrams
   */
  function lineHasWeight(item, weightGrams) {
    if (weightGrams == null) return false;
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    var parts = splitCsv(props.Weight != null ? String(props.Weight) : '');
    var qty = Number(item.quantity || 0);
    if (parts.length === 1 && qty > 1) {
      return normalizeWeightGrams(parts[0]) === weightGrams;
    }
    for (var i = 0; i < parts.length; i++) {
      if (normalizeWeightGrams(parts[i]) === weightGrams) return true;
    }
    return false;
  }

  /**
   * @param {Record<string, unknown>} cart
   * @param {string} sku
   * @param {number|null|undefined} weightGrams
   */
  function countWeightDemandInCart(cart, sku, weightGrams) {
    if (weightGrams == null) return 0;
    var items = Array.isArray(cart.items) ? cart.items : [];
    var total = 0;
    items.forEach(function (raw) {
      if (!raw) return;
      var item = /** @type {Record<string, unknown>} */ (raw);
      if (sku && String(itemSku(item)).toUpperCase() !== String(sku).toUpperCase()) return;
      var props =
        item.properties && typeof item.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (item.properties)
          : {};
      var parts = splitCsv(props.Weight != null ? String(props.Weight) : '');
      var qty = Number(item.quantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) return;
      if (parts.length === 1 && qty >= 1) {
        if (normalizeWeightGrams(parts[0]) === weightGrams) total += qty;
        return;
      }
      parts.forEach(function (part) {
        if (normalizeWeightGrams(part) === weightGrams) total += 1;
      });
    });
    return total;
  }

  /**
   * @param {Array<Record<string, unknown>>} conflicts
   * @param {Record<string, unknown>} cart
   */
  function pruneConflictsAgainstCart(conflicts, cart) {
    return conflicts
      .map(function (conflict) {
        var next = Object.assign({}, conflict);
        var demand = countWeightDemandInCart(cart, String(next.sku || ''), next.weight);
        next.requestedQuantity = demand;
        return next;
      })
      .filter(function (conflict) {
        var demand = Number(conflict.requestedQuantity || 0);
        if (demand <= 0) return false;
        var available =
          conflict.availableQuantity != null && Number.isFinite(Number(conflict.availableQuantity))
            ? Number(conflict.availableQuantity)
            : null;
        if (available != null && demand <= available) return false;
        return true;
      });
  }

  /**
   * Group flat conflict rows into one parent card per SKU.
   * @param {Array<Record<string, unknown>>} conflicts
   * @returns {Array<Record<string, unknown>>}
   */
  function groupConflictsBySku(conflicts) {
    /** @type {Record<string, Record<string, unknown>>} */
    var bySku = {};
    /** @type {string[]} */
    var order = [];
    (conflicts || []).forEach(function (conflict) {
      if (!conflict) return;
      var skuRaw = String(conflict.sku || '').trim();
      var skuKey = skuRaw ? skuRaw.toUpperCase() : 'UNKNOWN';
      if (!bySku[skuKey]) {
        bySku[skuKey] = {
          key: skuKey,
          sku: skuRaw || skuKey,
          productId: conflict.productId != null ? String(conflict.productId) : '',
          variantId: conflict.variantId != null ? conflict.variantId : null,
          productName: conflict.productName != null ? String(conflict.productName) : '',
          image: conflict.image != null ? String(conflict.image) : '',
          conflicts: [],
        };
        order.push(skuKey);
      }
      var group = bySku[skuKey];
      if (!group.productName && conflict.productName) group.productName = String(conflict.productName);
      if (!group.image && conflict.image) group.image = String(conflict.image);
      if (!group.productId && conflict.productId != null) group.productId = String(conflict.productId);
      if (group.variantId == null && conflict.variantId != null) group.variantId = conflict.variantId;
      /** @type {Array<Record<string, unknown>>} */ (group.conflicts).push(conflict);
    });
    return order.map(function (key) {
      return bySku[key];
    });
  }

  /** @param {number} count */
  function formatUnavailableBadge(count) {
    var n = Math.max(0, Math.floor(Number(count) || 0));
    return n === 1 ? '1 unavailable weight' : n + ' unavailable weights';
  }

  /**
   * Remove N occurrences of a weight from one cart line props.
   * @param {Record<string, unknown>} item
   * @param {number} weightGrams
   * @param {number} removeCount
   */
  function removeWeightFromLineItem(item, weightGrams, removeCount) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? Object.assign({}, /** @type {Record<string, unknown>} */ (item.properties))
        : {};
    var weightParts = splitCsv(props.Weight != null ? String(props.Weight) : '');
    var serialParts = splitCsv(
      props.SerialNumbers != null
        ? String(props.SerialNumbers)
        : props.serialNumbers != null
          ? String(props.serialNumbers)
          : ''
    );
    var qty = Number(item.quantity || 0);
    if (!Number.isFinite(qty) || qty < 1) qty = weightParts.length || 0;

    var removed = 0;
    /** @type {string[]} */
    var nextWeights = [];
    /** @type {string[]} */
    var nextSerials = [];

    if (weightParts.length === 1 && qty > 1) {
      var only = normalizeWeightGrams(weightParts[0]);
      if (only !== weightGrams) {
        return { changed: false, quantity: qty, properties: props, removed: 0 };
      }
      removed = Math.min(removeCount, qty);
      var nextQty = qty - removed;
      if (nextQty <= 0) {
        return { changed: true, quantity: 0, properties: props, removed: removed };
      }
      if (serialParts.length === qty) {
        nextSerials = serialParts.slice(removed);
      } else if (serialParts.length === 1) {
        nextSerials = serialParts;
      }
      props.Weight = weightParts[0];
      if (nextSerials.length) props.SerialNumbers = nextSerials.join(', ');
      else delete props.SerialNumbers;
      return { changed: true, quantity: nextQty, properties: props, removed: removed };
    }

    for (var i = 0; i < weightParts.length; i++) {
      var grams = normalizeWeightGrams(weightParts[i]);
      if (removed < removeCount && grams === weightGrams) {
        removed += 1;
        continue;
      }
      nextWeights.push(weightParts[i]);
      if (serialParts[i] != null && String(serialParts[i]).trim()) {
        nextSerials.push(String(serialParts[i]).trim());
      }
    }

    if (!removed) {
      return { changed: false, quantity: qty, properties: props, removed: 0 };
    }

    var nextQuantity = nextWeights.length;
    if (nextQuantity <= 0) {
      return { changed: true, quantity: 0, properties: props, removed: removed };
    }
    props.Weight = nextWeights.join(', ');
    if (nextSerials.length) props.SerialNumbers = nextSerials.join(', ');
    else delete props.SerialNumbers;
    return { changed: true, quantity: nextQuantity, properties: props, removed: removed };
  }

  /** @param {HTMLElement} container */
  function focusableList(container) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(nodes, function (el) {
      return (
        el instanceof HTMLElement &&
        !el.hasAttribute('hidden') &&
        (el.offsetParent !== null || (el.getClientRects && el.getClientRects().length > 0))
      );
    });
  }

  /**
   * @param {{
   *   root: HTMLElement,
   *   cfg: Record<string, unknown>,
   *   fetchJson: Function,
   *   applyCartToDom: Function,
   *   publishCartCountUpdate: Function,
   *   showCartFeedback: Function,
   *   showToast: Function,
   * }} deps
   */
  function createController(deps) {
    var root = deps.root;
    var cfg = deps.cfg;
    /** @type {Array<Record<string, unknown>>} */
    var conflicts = [];
    /** @type {Record<string, unknown>|null} */
    var lastCart = null;
    var htmlOverflow = '';
    var bodyOverflow = '';
    /** @type {Element|null} */
    var lastFocus = null;
    /** @type {((e: KeyboardEvent) => void)|null} */
    var escHandler = null;
    /** @type {((e: KeyboardEvent) => void)|null} */
    var tabHandler = null;
    var removing = false;
    var editBusy = false;
    var WEIGHT_PREVIEW_LIMIT = 4;
    /** @type {string} */
    var expandedSkuKey = '';
    /** @type {Record<string, boolean>} */
    var weightsExpandedBySku = {};
    /** When Shopify regenerates line keys mid-conflict-flow, defer full page remount. */
    var needsCartRemount = false;

    /** @type {{
     *   lineKey: string,
     *   productId: string,
     *   sku: string,
     *   targetQty: number,
     *   originalConflictQty: number,
     *   removedQty: number,
     *   requiredReplacementQty: number,
     *   options: Array<Record<string, unknown>>,
     *   selectedIds: string[],
     *   baselineSelectedIds: string[],
     *   baselineCount: number,
     *   baselineByWeight: Record<string, number>,
     *   originalBaselineByWeight: Record<string, number>,
     *   keepWeights: string[],
     *   keepSerials: string[],
     *   conflictRows: Array<{ id: string, weight: number|null, label: string, availableQuantity: number, requestedQuantity: number }>,
     *   requestSeq: number,
     *   conflictId: string,
     *   extraLineKeys?: string[],
     *   conflictWeights?: Array<number|null>
     * }|null} */
    var editState = null;

    /**
     * Expand cart line Weight/Serial CSV into one unit per quantity slot.
     * @param {Record<string, unknown>} line
     * @returns {Array<{ label: string, serial: string, weight: number|null }>}
     */
    function expandLineWeightUnits(line) {
      /** @type {Array<{ label: string, serial: string, weight: number|null }>} */
      var units = [];
      if (!line) return units;
      var props =
        line.properties && typeof line.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (line.properties)
          : {};
      var serials = splitCsv(
        props.SerialNumbers != null
          ? String(props.SerialNumbers)
          : props.serialNumbers != null
            ? String(props.serialNumbers)
            : ''
      );
      var labels = splitCsv(props.Weight != null ? String(props.Weight) : '');
      var qty = Math.max(0, Math.floor(Number(line.quantity) || 0));
      if (labels.length === 1 && qty > 1 && serials.length === qty) {
        for (var q = 0; q < qty; q++) {
          units.push({
            label: labels[0],
            serial: serials[q] || '',
            weight: normalizeWeightGrams(labels[0]),
          });
        }
      } else if (labels.length === 1 && qty > 1) {
        for (var r = 0; r < qty; r++) {
          units.push({
            label: labels[0],
            serial: serials[r] || serials[0] || '',
            weight: normalizeWeightGrams(labels[0]),
          });
        }
      } else {
        labels.forEach(function (label, index) {
          units.push({
            label: label,
            serial: serials[index] || '',
            weight: normalizeWeightGrams(label),
          });
        });
      }
      return units;
    }

    /**
     * Build unavailable conflict rows + keepable cart units for Edit All Weights.
     * Conflict rows come from Sync conflict data (not live WMS availableOptions).
     * @param {Array<Record<string, unknown>>} skuLines
     * @param {Array<Record<string, unknown>>} sourceConflicts
     */
    function buildEditReplacementContext(skuLines, sourceConflicts) {
      /** @type {Record<string, { id: string, weight: number|null, label: string, availableQuantity: number, requestedQuantity: number }>} */
      var mergedRows = {};
      /** @type {Record<string, number>} */
      var dropByWeight = {};
      var originalConflictQty = 0;

      (sourceConflicts || []).forEach(function (c) {
        if (!c) return;
        var w = normalizeWeightGrams(c.weight);
        var req = Math.max(0, Math.floor(Number(c.requestedQuantity) || 0));
        if (req < 1) req = 1;
        var available =
          c.availableQuantity != null && Number.isFinite(Number(c.availableQuantity))
            ? Math.max(0, Math.floor(Number(c.availableQuantity)))
            : 0;
        var key = w != null ? w.toFixed(3) : String(c.weight || '').trim() || 'unknown';
        if (!mergedRows[key]) {
          mergedRows[key] = {
            id: 'conflict-' + key,
            weight: w,
            label: w != null ? formatConflictWeight(w) : String(c.weight || key),
            availableQuantity: available,
            requestedQuantity: req,
          };
        } else {
          mergedRows[key].requestedQuantity += req;
          if (available < mergedRows[key].availableQuantity) {
            mergedRows[key].availableQuantity = available;
          }
        }
        if (w != null) {
          dropByWeight[key] = (dropByWeight[key] || 0) + req;
        }
        originalConflictQty += req;
      });

      /** @type {Array<{ id: string, weight: number|null, label: string, availableQuantity: number, requestedQuantity: number }>} */
      var conflictRows = Object.keys(mergedRows)
        .map(function (key) {
          return mergedRows[key];
        })
        .sort(function (a, b) {
          return (a.weight || 0) - (b.weight || 0);
        });

      /** @type {string[]} */
      var keepWeights = [];
      /** @type {string[]} */
      var keepSerials = [];
      /** @type {Record<string, number>} */
      var remainingDrop = Object.assign({}, dropByWeight);
      /** @type {Record<string, number>} */
      var baselineByWeight = {};

      (skuLines || []).forEach(function (line) {
        expandLineWeightUnits(line).forEach(function (unit) {
          var key = unit.weight != null ? unit.weight.toFixed(3) : '';
          if (key && remainingDrop[key] > 0) {
            remainingDrop[key] -= 1;
            return;
          }
          var weightLabel =
            unit.weight != null ? unit.weight.toFixed(3) : String(unit.label || '').trim();
          if (!weightLabel) return;
          keepWeights.push(weightLabel);
          keepSerials.push(String(unit.serial || '').trim());
          if (unit.weight != null) {
            baselineByWeight[key] = (baselineByWeight[key] || 0) + 1;
          }
        });
      });

      return {
        conflictRows: conflictRows,
        originalConflictQty: originalConflictQty,
        requiredReplacementQty: originalConflictQty,
        keepWeights: keepWeights,
        keepSerials: keepSerials,
        baselineByWeight: baselineByWeight,
        conflictWeights: conflictRows.map(function (row) {
          return row.weight;
        }),
      };
    }

    function getOriginalBaselineCountForWeight(weightKey) {
      if (!editState) return 0;
      var source = editState.originalBaselineByWeight || editState.baselineByWeight || {};
      return Math.max(0, Math.floor(Number(source[weightKey]) || 0));
    }

    function getSelectedCountByWeight() {
      /** @type {Record<string, number>} */
      var counts = {};
      if (!editState || !Array.isArray(editState.selectedIds)) return counts;
      editState.selectedIds.forEach(function (id) {
        var opt = editState.options.find(function (row) {
          return String(row.id) === String(id);
        });
        if (!opt) return;
        var weight = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.label);
        var key = weight != null ? weight.toFixed(3) : String(opt.label || '');
        if (!key) return;
        counts[key] = (counts[key] || 0) + 1;
      });
      return counts;
    }

    /**
     * Only quantities above the original cart baseline count as replacements.
     * Reducing a green cart-selected weight below its baseline does not create
     * negative replacement credit.
     */
    function getReplacementAddedQty() {
      if (!editState) return 0;
      var baselineMap = editState.originalBaselineByWeight || editState.baselineByWeight || {};
      var currentByWeight = getSelectedCountByWeight();
      var added = 0;
      Object.keys(currentByWeight).forEach(function (key) {
        var current = Math.max(0, Math.floor(Number(currentByWeight[key]) || 0));
        var baseline = Math.max(0, Math.floor(Number(baselineMap[key]) || 0));
        added += Math.max(0, current - baseline);
      });
      return added;
    }

    function isBaselineOptionId(optionId) {
      if (!editState || !Array.isArray(editState.baselineSelectedIds)) return false;
      return editState.baselineSelectedIds.indexOf(String(optionId)) >= 0;
    }

    function releaseBaselineOptionId(optionId) {
      if (!editState || !optionId) return;
      var id = String(optionId);
      if (!Array.isArray(editState.baselineSelectedIds)) return;
      var idx = editState.baselineSelectedIds.indexOf(id);
      if (idx < 0) return;
      editState.baselineSelectedIds.splice(idx, 1);
      editState.baselineCount = editState.baselineSelectedIds.length;
      var opt = editState.options.find(function (row) {
        return String(row.id) === id;
      });
      if (!opt || !editState.baselineByWeight) return;
      var weight = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.label);
      var key = weight != null ? weight.toFixed(3) : '';
      if (!key) return;
      var next = Math.max(0, Math.floor(Number(editState.baselineByWeight[key]) || 0) - 1);
      if (next <= 0) delete editState.baselineByWeight[key];
      else editState.baselineByWeight[key] = next;
    }

    function getEditResolutionStats() {
      if (!editState) {
        return {
          original: 0,
          removed: 0,
          replacement: 0,
          resolved: 0,
          remaining: 0,
          replacementCap: 0,
        };
      }
      var original = Math.max(0, Math.floor(Number(editState.originalConflictQty) || 0));
      var removed = Math.max(0, Math.floor(Number(editState.removedQty) || 0));
      var replacement = getReplacementAddedQty();
      var resolved = Math.min(original, removed + replacement);
      var remaining = Math.max(0, original - resolved);
      var replacementCap = Math.max(0, original - removed);
      return {
        original: original,
        removed: removed,
        replacement: replacement,
        resolved: resolved,
        remaining: remaining,
        replacementCap: replacementCap,
      };
    }

    function trimReplacementSelectionToCap() {
      if (!editState) return;
      var stats = getEditResolutionStats();
      while (getReplacementAddedQty() > stats.replacementCap) {
        var removedOne = false;
        for (var i = editState.selectedIds.length - 1; i >= 0; i--) {
          var id = String(editState.selectedIds[i]);
          var opt = editState.options.find(function (row) {
            return String(row.id) === id;
          });
          if (!opt) continue;
          var weight = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.label);
          var key = weight != null ? weight.toFixed(3) : String(opt.label || '');
          var currentByWeight = getSelectedCountByWeight();
          var current = Math.max(0, Math.floor(Number(currentByWeight[key]) || 0));
          var originalBaseline = getOriginalBaselineCountForWeight(key);
          // Only trim quantities that sit above the original cart baseline.
          if (current <= originalBaseline) continue;
          editState.selectedIds.splice(i, 1);
          removedOne = true;
          break;
        }
        if (!removedOne) break;
      }
      editState.requiredReplacementQty = stats.replacementCap;
      editState.targetQty =
        stats.replacementCap + Math.max(0, Math.floor(Number(editState.baselineCount) || 0));
    }

    /**
     * Top-right unresolved guidance (replace or remove). Kept in sync with Resolved Items.
     */
    function updateConflictGuidanceMessage() {
      var modal = getEditModal();
      if (!(modal instanceof HTMLElement) || !editState) return;
      var meta = modal.querySelector('[data-b2b-cart-edit-weight-conflict-meta]');
      if (!(meta instanceof HTMLElement)) return;
      var stats = getEditResolutionStats();
      var remaining = Math.max(0, Math.floor(Number(stats.remaining) || 0));
      if (remaining < 1) {
        meta.textContent = '';
        return;
      }
      meta.textContent =
        remaining === 1
          ? '1 item needs to be replaced or removed'
          : remaining + ' items need to be replaced or removed';
    }

    function renderEditConflictSection() {
      var modal = getEditModal();
      if (!(modal instanceof HTMLElement) || !editState) return;
      var section = modal.querySelector('[data-b2b-cart-edit-weight-conflicts]');
      var tbody = modal.querySelector('[data-b2b-cart-edit-weight-conflict-rows]');
      var meta = modal.querySelector('[data-b2b-cart-edit-weight-conflict-meta]');
      var rows = Array.isArray(editState.conflictRows) ? editState.conflictRows : [];
      if (!(section instanceof HTMLElement) || !(tbody instanceof HTMLElement)) return;
      if (!rows.length) {
        section.hidden = true;
        tbody.innerHTML = '';
        if (meta) meta.textContent = '';
        return;
      }
      section.hidden = false;
      updateConflictGuidanceMessage();
      tbody.innerHTML = rows
        .map(function (row) {
          return (
            '<tr class="cart-sync-weight-editor__conflict-row">' +
            '<td data-label="Weight" class="cart-sync-weight-editor__conflict-weight">' +
            '<span class="cart-sync-weight-editor__conflict-weight-text">' +
            escapeHtml(row.label) +
            '</span>' +
            '<span class="cart-sync-weight-editor__unavailable-badge">Unavailable</span>' +
            '</td>' +
            '<td data-label="Available Qty" class="cart-sync-weight-editor__conflict-available">' +
            escapeHtml(String(row.availableQuantity)) +
            '</td>' +
            '<td data-label="Selected Qty" class="cart-sync-weight-editor__conflict-selected">' +
            escapeHtml(String(row.requestedQuantity)) +
            '</td>' +
            '<td data-label="Action" class="cart-sync-weight-editor__conflict-action">' +
            '<button type="button" class="cart-sync-weight-editor__remove-btn" data-b2b-cart-edit-weight-remove-conflict data-conflict-id="' +
            escapeHtml(String(row.id)) +
            '">' +
            '<span class="cart-sync-weight-editor__remove-icon" aria-hidden="true">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg></span>' +
            '<span>Remove</span>' +
            '</button></td></tr>'
          );
        })
        .join('');
    }

    /**
     * Mark an unavailable conflict row as resolved by removal (deferred until Update Selection).
     * Valid keepable weights are untouched.
     * @param {string} conflictId
     * @param {HTMLButtonElement|null} trigger
     */
    function removeConflictRowFromEditor(conflictId, trigger) {
      if (!editState || editBusy || !conflictId) return;
      var idx = -1;
      for (var i = 0; i < editState.conflictRows.length; i++) {
        if (String(editState.conflictRows[i].id) === String(conflictId)) {
          idx = i;
          break;
        }
      }
      if (idx < 0) return;

      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
        trigger.classList.add('is-loading');
      }

      var row = editState.conflictRows[idx];
      var removeCount = Math.max(1, Math.floor(Number(row.requestedQuantity) || 1));
      editState.conflictRows.splice(idx, 1);
      editState.removedQty = Math.max(0, Math.floor(Number(editState.removedQty) || 0)) + removeCount;
      trimReplacementSelectionToCap();
      renderEditConflictSection();
      var modal = getEditModal();
      var search =
        modal instanceof HTMLElement
          ? modal.querySelector('[data-b2b-cart-edit-weight-search]')
          : null;
      if (editState.options && editState.options.length) {
        renderEditOptions(search instanceof HTMLInputElement ? search.value : '');
      }
      updateEditSummary();
    }

    function getAvailabilityModal() {
      var id = cfg.syncAvailabilityModalId != null ? String(cfg.syncAvailabilityModalId) : '';
      var el = id ? document.getElementById(id) : null;
      if (el instanceof HTMLElement) return el;
      return root.querySelector('[data-b2b-sync-availability-modal]');
    }

    function getEditModal() {
      var id = cfg.editWeightModalId != null ? String(cfg.editWeightModalId) : '';
      var el = id ? document.getElementById(id) : null;
      if (el instanceof HTMLElement) return el;
      return root.querySelector('[data-b2b-cart-edit-weight-modal]');
    }

    function unlockBody() {
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
    }

    function lockBody() {
      htmlOverflow = document.documentElement.style.overflow;
      bodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    function clearAvailabilityUiListeners() {
      if (escHandler) {
        document.removeEventListener('keydown', escHandler, true);
        escHandler = null;
      }
      if (tabHandler) {
        var modal = getAvailabilityModal();
        var dialog = modal && modal.querySelector('[role="dialog"]');
        if (dialog) dialog.removeEventListener('keydown', tabHandler);
        tabHandler = null;
      }
    }

    function resetConflicts() {
      conflicts = [];
      lastCart = null;
      expandedSkuKey = '';
      weightsExpandedBySku = {};
    }

    function closeAvailabilityModal() {
      var shouldRemount = needsCartRemount;
      needsCartRemount = false;
      var modal = getAvailabilityModal();
      if (!(modal instanceof HTMLElement)) {
        resetConflicts();
        if (shouldRemount) win.location.reload();
        return;
      }
      clearAvailabilityUiListeners();
      modal.classList.remove('is-open');
      modal.setAttribute('hidden', '');
      modal.setAttribute('aria-hidden', 'true');
      unlockBody();
      resetConflicts();
      var list = modal.querySelector('[data-b2b-sync-availability-sku-list]');
      if (list) list.innerHTML = '';
      if (lastFocus && typeof /** @type {HTMLElement} */ (lastFocus).focus === 'function') {
        try {
          /** @type {HTMLElement} */ (lastFocus).focus();
        } catch (_) {}
      }
      lastFocus = null;
      // Remount only after the conflict workflow fully ends (keys may have regenerated).
      if (shouldRemount) win.location.reload();
    }

    function getSkuGroups() {
      return groupConflictsBySku(conflicts);
    }

    /**
     * Keep a single source of truth for which SKU accordion panel is open.
     * @param {{ preferFirstIfEmpty?: boolean }} [opts]
     */
    function ensureExpandedSku(opts) {
      var options = opts || {};
      var groups = getSkuGroups();
      if (!groups.length) {
        expandedSkuKey = '';
        return;
      }
      var stillPresent = groups.some(function (g) {
        return String(g.key) === String(expandedSkuKey);
      });
      if (stillPresent) return;
      // Allow intentional collapse (empty key). Only auto-expand when:
      // - opening the parent modal, or
      // - the previously expanded SKU was resolved/removed (non-empty stale key).
      if (expandedSkuKey === '' && !options.preferFirstIfEmpty) return;
      expandedSkuKey = String(groups[0].key);
    }

    /** @param {boolean} isOpen */
    function chevronIconHtml(isOpen) {
      // Distinct up/down chevrons derived from the same isExpanded state as the panel.
      if (isOpen) {
        return (
          '<svg class="b2b-sync-availability-modal__chevron-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">' +
          '<path d="M6 14.5L12 8.5l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>'
        );
      }
      return (
        '<svg class="b2b-sync-availability-modal__chevron-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">' +
        '<path d="M6 9.5l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>'
      );
    }

    /** @param {string} skuKey */
    function skuPanelDomId(skuKey) {
      return (
        'b2b-sync-avail-panel-' +
        String(skuKey || '')
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '-')
      );
    }

    function renderWeightRows(group, showAll) {
      var rows = Array.isArray(group.conflicts) ? group.conflicts : [];
      var visible = showAll ? rows : rows.slice(0, WEIGHT_PREVIEW_LIMIT);
      return visible
        .map(function (conflict) {
          var available =
            conflict.availableQuantity != null && Number.isFinite(Number(conflict.availableQuantity))
              ? String(Number(conflict.availableQuantity))
              : '—';
          var requested =
            conflict.requestedQuantity != null && Number.isFinite(Number(conflict.requestedQuantity))
              ? String(Number(conflict.requestedQuantity))
              : '—';
          var insufficient =
            conflict.availableQuantity != null &&
            conflict.requestedQuantity != null &&
            Number(conflict.availableQuantity) < Number(conflict.requestedQuantity);
          return (
            '<tr>' +
            '<td data-label="Weight Options" class="b2b-sync-availability-modal__weight">' +
            escapeHtml(formatConflictWeight(/** @type {number|null} */ (conflict.weight))) +
            '</td>' +
            '<td data-label="Requested" class="b2b-sync-availability-modal__requested">' +
            escapeHtml(requested) +
            '</td>' +
            '<td data-label="Available" class="b2b-sync-availability-modal__available' +
            (insufficient || Number(conflict.availableQuantity) === 0
              ? ' b2b-sync-availability-modal__available--error'
              : '') +
            '">' +
            escapeHtml(available) +
            '</td>' +
            '</tr>'
          );
        })
        .join('');
    }

    function renderConflicts() {
      var modal = getAvailabilityModal();
      if (!(modal instanceof HTMLElement)) return;
      var list = modal.querySelector('[data-b2b-sync-availability-sku-list]');
      if (!(list instanceof HTMLElement)) return;
      ensureExpandedSku();
      var groups = getSkuGroups();
      if (!groups.length) {
        list.innerHTML = '';
        return;
      }

      list.innerHTML = groups
        .map(function (group) {
          var skuKey = String(group.key);
          var isOpen = String(expandedSkuKey) === skuKey;
          var conflictRows = Array.isArray(group.conflicts) ? group.conflicts : [];
          var count = conflictRows.length;
          var showAllWeights = !!weightsExpandedBySku[skuKey];
          var hiddenCount = Math.max(0, count - WEIGHT_PREVIEW_LIMIT);
          var name = String(group.productName || group.sku || 'Product');
          var image = String(group.image || '');
          var skuLabel = String(group.sku || skuKey);
          var panelId = skuPanelDomId(skuKey);
          var toggleLabel = isOpen
            ? 'Hide unavailable weights for SKU ' + skuLabel
            : 'Show unavailable weights for SKU ' + skuLabel;
          var thumb = image
            ? '<img class="b2b-sync-availability-modal__thumb" src="' +
              escapeHtml(image) +
              '" alt="" width="48" height="48" loading="lazy">'
            : '<span class="b2b-sync-availability-modal__thumb b2b-sync-availability-modal__thumb--placeholder" aria-hidden="true"></span>';
          var moreHtml = '';
          if (hiddenCount > 0) {
            moreHtml =
              '<button type="button" class="b2b-sync-availability-modal__more" data-b2b-sync-availability-more data-sku-key="' +
              escapeHtml(skuKey) +
              '">' +
              (showAllWeights ? 'Show less' : '+' + hiddenCount + ' more weights') +
              '</button>';
          }

          return (
            '<article class="b2b-sync-availability-modal__sku-card' +
            (isOpen ? ' is-expanded' : '') +
            '" data-sku-key="' +
            escapeHtml(skuKey) +
            '" role="listitem">' +
            '<button type="button" class="b2b-sync-availability-modal__sku-toggle" data-b2b-sync-availability-toggle data-sku-key="' +
            escapeHtml(skuKey) +
            '" aria-expanded="' +
            (isOpen ? 'true' : 'false') +
            '" aria-controls="' +
            escapeHtml(panelId) +
            '" aria-label="' +
            escapeHtml(toggleLabel) +
            '">' +
            '<span class="b2b-sync-availability-modal__sku-main">' +
            thumb +
            '<span class="b2b-sync-availability-modal__sku-copy">' +
            '<span class="b2b-sync-availability-modal__sku-title">' +
            escapeHtml(name) +
            '</span>' +
            '<span class="b2b-sync-availability-modal__sku-code">SKU: ' +
            escapeHtml(skuLabel) +
            '</span>' +
            '</span></span>' +
            '<span class="b2b-sync-availability-modal__sku-meta">' +
            '<span class="b2b-sync-availability-modal__badge">' +
            escapeHtml(formatUnavailableBadge(count)) +
            '</span>' +
            '<span class="b2b-sync-availability-modal__chevron" aria-hidden="true">' +
            chevronIconHtml(isOpen) +
            '</span>' +
            '</span></button>' +
            (isOpen
              ? '<div class="b2b-sync-availability-modal__sku-panel" id="' +
                escapeHtml(panelId) +
                '" role="region" aria-label="Unavailable weights for SKU ' +
                escapeHtml(skuLabel) +
                '">' +
                '<div class="b2b-sync-availability-modal__sku-panel-grid">' +
                '<div class="b2b-sync-availability-modal__weights-wrap">' +
                '<table class="b2b-sync-availability-modal__weights-table">' +
                '<thead><tr><th scope="col">Weight Options</th><th scope="col">Requested</th><th scope="col">Available</th></tr></thead>' +
                '<tbody>' +
                renderWeightRows(group, showAllWeights) +
                '</tbody></table>' +
                moreHtml +
                '</div>' +
                '<aside class="b2b-sync-availability-modal__quick-actions">' +
                '<p class="b2b-sync-availability-modal__quick-label">Quick actions for this SKU</p>' +
                '<button type="button" class="b2b-sync-availability-modal__edit-all" data-b2b-sync-availability-edit-sku data-sku-key="' +
                escapeHtml(skuKey) +
                '">Edit All Weights</button>' +
                '<button type="button" class="b2b-sync-availability-modal__remove-sku" data-b2b-sync-availability-remove-sku data-sku-key="' +
                escapeHtml(skuKey) +
                '">Remove Entire SKU</button>' +
                '</aside></div></div>'
              : '<div class="b2b-sync-availability-modal__sku-panel" id="' +
                escapeHtml(panelId) +
                '" hidden></div>') +
            '</article>'
          );
        })
        .join('');
    }

    function openAvailabilityModal(nextConflicts, cart, opts) {
      var options = opts || {};
      var modal = getAvailabilityModal();
      if (!(modal instanceof HTMLElement) || !nextConflicts.length) return false;
      conflicts = nextConflicts.slice();
      lastCart = cart || null;
      if (!options.preserveUiState) weightsExpandedBySku = {};
      ensureExpandedSku({ preferFirstIfEmpty: true });
      if (!options.preserveFocus) lastFocus = document.activeElement;
      renderConflicts();
      if (!options.preserveLock) lockBody();
      modal.removeAttribute('hidden');
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');

      clearAvailabilityUiListeners();
      escHandler = function (e) {
        if (e.key !== 'Escape') return;
        // Child weight editor owns Escape while it is open.
        var editModal = getEditModal();
        if (editModal instanceof HTMLElement && !editModal.hidden) return;
        e.preventDefault();
        closeAvailabilityModal();
      };
      document.addEventListener('keydown', escHandler, true);

      var dialog = modal.querySelector('[role="dialog"]');
      if (dialog instanceof HTMLElement) {
        tabHandler = function (e) {
          if (e.key !== 'Tab') return;
          var list = focusableList(dialog);
          if (!list.length) return;
          var first = list[0];
          var last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        };
        dialog.addEventListener('keydown', tabHandler);
        win.requestAnimationFrame(function () {
          var continueBtn = modal.querySelector('[data-b2b-sync-availability-continue]');
          if (continueBtn instanceof HTMLElement) continueBtn.focus();
          else dialog.focus();
        });
      }
      return true;
    }

    function setModalBusy(busy) {
      var modal = getAvailabilityModal();
      if (!(modal instanceof HTMLElement)) return;
      modal.querySelectorAll('[data-b2b-sync-availability-cancel], [data-b2b-sync-availability-continue], [data-b2b-sync-availability-close]').forEach(
        function (btn) {
          if (btn instanceof HTMLButtonElement) btn.disabled = !!busy;
        }
      );
    }

    /**
     * @param {Record<string, unknown>} cart
     * @param {string} sourceId
     * @param {{ previousKeys?: Record<string, 1> }} [opts]
     */
    function afterCartMutation(cart, sourceId, opts) {
      lastCart = cart;
      var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
      var nextKeys = {};
      (Array.isArray(cart.items) ? cart.items : []).forEach(function (item) {
        if (item && item.key != null) nextKeys[String(item.key)] = 1;
      });
      var previousKeys = (opts && opts.previousKeys) || {};
      var hasUnknownKey = false;
      Object.keys(nextKeys).forEach(function (key) {
        if (!previousKeys[key]) hasUnknownKey = true;
      });

      conflicts = pruneConflictsAgainstCart(conflicts, cart);

      // Shopify regenerates line keys when line-item properties change.
      // Do NOT reload while unresolved conflicts remain — that closes the parent
      // modal and forces the SR to click Sync Order again. Defer remount until
      // the conflict workflow fully ends.
      if (count > 0 && hasUnknownKey) {
        if (conflicts.length) {
          needsCartRemount = true;
          deps.publishCartCountUpdate(cart, sourceId);
          try {
            deps.applyCartToDom(root, cart, cfg);
          } catch (_) {}
          ensureExpandedSku();
          renderConflicts();
          return;
        }
        win.location.reload();
        return;
      }

      deps.applyCartToDom(root, cart, cfg);
      deps.publishCartCountUpdate(cart, sourceId);
      if (!conflicts.length) {
        closeAvailabilityModal();
        return;
      }
      ensureExpandedSku();
      renderConflicts();
    }

    /**
     * @param {Record<string, unknown>} conflict
     */
    async function removeConflict(conflict) {
      if (removing || !conflict) return;
      var cart = lastCart;
      if (!cart || !Array.isArray(cart.items)) {
        var refreshed = await deps.fetchJson(String(cfg.cartJsUrl || '/cart.js'));
        if (!refreshed.res.ok) {
          deps.showCartFeedback(root, 'Could not refresh cart before removing the weight.');
          return;
        }
        cart = /** @type {Record<string, unknown>} */ (refreshed.data);
        lastCart = cart;
      }

      var remaining = Math.max(1, Math.floor(Number(conflict.requestedQuantity) || 1));
      var weightGrams = normalizeWeightGrams(conflict.weight);
      if (weightGrams == null) {
        deps.showCartFeedback(root, 'Could not identify the unavailable weight to remove.');
        return;
      }

      removing = true;
      setModalBusy(true);
      var modal = getAvailabilityModal();
      var removeBtn =
        modal &&
        modal.querySelector(
          'tr[data-conflict-id="' +
            String(conflict.id).replace(/\\/g, '\\\\').replace(/"/g, '\\"') +
            '"] [data-b2b-sync-availability-remove]'
        );
      if (removeBtn instanceof HTMLButtonElement) {
        removeBtn.disabled = true;
        removeBtn.setAttribute('aria-busy', 'true');
      }

      try {
        /** @type {Record<string, unknown>|null} */
        var latestCart = /** @type {Record<string, unknown>} */ (cart);
        /** @type {Record<string, 1>} */
        var previousKeys = {};
        (Array.isArray(cart.items) ? cart.items : []).forEach(function (item) {
          if (item && item.key != null) previousKeys[String(item.key)] = 1;
        });
        var initialRemaining = remaining;
        var guard = 0;
        while (remaining > 0 && latestCart && guard < 20) {
          guard += 1;
          var liveItems = Array.isArray(latestCart.items) ? latestCart.items : [];
          var target = null;
          for (var i = 0; i < liveItems.length; i++) {
            var candidate = /** @type {Record<string, unknown>} */ (liveItems[i]);
            if (!candidate) continue;
            if (
              conflict.sku &&
              String(itemSku(candidate)).toUpperCase() !== String(conflict.sku).toUpperCase()
            ) {
              continue;
            }
            if (lineHasWeight(candidate, weightGrams)) {
              target = candidate;
              break;
            }
          }
          if (!target) break;

          var result = removeWeightFromLineItem(target, weightGrams, remaining);
          if (!result.changed || !result.removed) break;
          remaining -= result.removed;

          var body;
          if (result.quantity <= 0) {
            body = { id: String(target.key), quantity: 0 };
          } else {
            body = {
              id: String(target.key),
              quantity: result.quantity,
              properties: result.properties,
            };
          }

          var change = await deps.fetchJson(String(cfg.cartChangeJsUrl || '/cart/change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          var changedCart = /** @type {Record<string, unknown>} */ (change.data);
          if (!change.res.ok || changedCart.errors) {
            var err =
              (typeof changedCart.message === 'string' && changedCart.message) ||
              (typeof changedCart.errors === 'string' && changedCart.errors) ||
              'Could not remove the unavailable weight.';
            deps.showCartFeedback(root, err);
            return;
          }
          latestCart = changedCart;
        }

        if (remaining === initialRemaining) {
          deps.showCartFeedback(root, 'Could not find that weight in the cart.');
          conflicts = pruneConflictsAgainstCart(conflicts, latestCart || cart);
          if (!conflicts.length) closeAvailabilityModal();
          else {
            ensureExpandedSku();
            renderConflicts();
          }
          return;
        }

        if (latestCart) afterCartMutation(latestCart, 'b2b-cart-review:availability-remove', { previousKeys: previousKeys });
      } catch (_) {
        deps.showCartFeedback(root, 'Network error while removing the unavailable weight.');
      } finally {
        removing = false;
        setModalBusy(false);
        if (removeBtn instanceof HTMLButtonElement) {
          removeBtn.disabled = false;
          removeBtn.removeAttribute('aria-busy');
        }
      }
    }

    /**
     * @param {string} skuKey
     * @returns {Record<string, unknown>|null}
     */
    function findSkuGroup(skuKey) {
      var groups = getSkuGroups();
      for (var i = 0; i < groups.length; i++) {
        if (String(groups[i].key) === String(skuKey)) return groups[i];
      }
      return null;
    }

    /**
     * @param {Record<string, unknown>} cart
     * @param {string} sku
     * @returns {Array<Record<string, unknown>>}
     */
    function findCartLinesForSku(cart, sku) {
      var safeSku = String(sku || '').trim().toUpperCase();
      if (!safeSku || !cart || !Array.isArray(cart.items)) return [];
      return cart.items
        .map(function (raw) {
          return /** @type {Record<string, unknown>} */ (raw);
        })
        .filter(function (item) {
          return item && String(itemSku(item)).toUpperCase() === safeSku;
        });
    }

    /**
     * @param {Record<string, unknown>} skuGroup
     * @param {HTMLButtonElement|null} [trigger]
     */
    async function removeEntireSku(skuGroup, trigger) {
      if (removing || !skuGroup) return;
      var cart = lastCart;
      if (!cart || !Array.isArray(cart.items)) {
        var refreshed = await deps.fetchJson(String(cfg.cartJsUrl || '/cart.js'));
        if (!refreshed.res.ok) {
          deps.showCartFeedback(root, 'Could not refresh cart before removing the SKU.');
          return;
        }
        cart = /** @type {Record<string, unknown>} */ (refreshed.data);
        lastCart = cart;
      }

      var lines = findCartLinesForSku(cart, String(skuGroup.sku || skuGroup.key || ''));
      if (!lines.length) {
        conflicts = conflicts.filter(function (c) {
          return String(c.sku || '').trim().toUpperCase() !== String(skuGroup.key);
        });
        if (!conflicts.length) closeAvailabilityModal();
        else {
          ensureExpandedSku();
          renderConflicts();
        }
        return;
      }

      /** @type {Record<string, number>} */
      var updates = {};
      /** @type {Record<string, 1>} */
      var previousKeys = {};
      (Array.isArray(cart.items) ? cart.items : []).forEach(function (item) {
        if (item && item.key != null) previousKeys[String(item.key)] = 1;
      });
      lines.forEach(function (line) {
        var key = String(line.key || '').trim();
        if (key) updates[key] = 0;
      });
      if (!Object.keys(updates).length) return;

      removing = true;
      setModalBusy(true);
      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
      }

      try {
        var latestCart = null;
        if (typeof deps.updateCartByKeys === 'function') {
          latestCart = await deps.updateCartByKeys(updates, { rootBusy: false });
        } else {
          var updateUrl = String(cfg.cartUpdateJsUrl || '/cart/update.js');
          var result = await deps.fetchJson(updateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates }),
          });
          var data = /** @type {Record<string, unknown>} */ (result.data);
          if (!result.res.ok || data.errors) {
            deps.showCartFeedback(root, 'Could not remove the selected SKU.');
            return;
          }
          latestCart = data;
        }
        if (!latestCart) {
          deps.showCartFeedback(root, 'Could not remove the selected SKU.');
          return;
        }
        afterCartMutation(
          /** @type {Record<string, unknown>} */ (latestCart),
          'b2b-cart-review:availability-remove-sku',
          { previousKeys: previousKeys }
        );
      } catch (_) {
        deps.showCartFeedback(root, 'Network error while removing the SKU.');
      } finally {
        removing = false;
        setModalBusy(false);
        if (trigger) {
          trigger.disabled = false;
          trigger.removeAttribute('aria-busy');
        }
      }
    }

    function getWmsApi() {
      return win.AOneWmsAvailability || win.WMSInventory || null;
    }

    function closeEditWeightModal(opts) {
      var reopen = !opts || opts.reopenAvailability !== false;
      var modal = getEditModal();
      if (modal instanceof HTMLElement) {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        modal.classList.remove('is-loading');
        modal.removeAttribute('data-edit-weight-ready');
      }
      document.body.classList.remove('order-sheet-weight-modal-open');
      editState = null;
      editBusy = false;
      // Close ONLY the child editor. Parent stays open when unresolved conflicts remain.
      if (!reopen || !conflicts.length) return;
      var availabilityModal = getAvailabilityModal();
      if (
        availabilityModal instanceof HTMLElement &&
        availabilityModal.classList.contains('is-open') &&
        !availabilityModal.hasAttribute('hidden')
      ) {
        // Parent already visible — refresh conflict list / auto-advance in place.
        ensureExpandedSku({ preferFirstIfEmpty: true });
        renderConflicts();
        return;
      }
      openAvailabilityModal(conflicts, lastCart || undefined, {
        preserveLock: true,
        preserveFocus: true,
        preserveUiState: true,
      });
    }

    /**
     * Toggle skeleton vs final editor UI.
     * Ready means WMS options merge + baseline selection finished (ready/empty),
     * not merely that the fetch promise settled.
     * @param {'loading'|'ready'|'empty'|'error'} state
     */
    function setEditFetchUi(state) {
      var modal = getEditModal();
      if (!(modal instanceof HTMLElement)) return;

      var isLoading = state === 'loading';
      var isReady = state === 'ready';
      var isEmpty = state === 'empty';
      var isError = state === 'error';
      var showFinalContent = isReady || isEmpty;

      var skeleton = modal.querySelector('[data-b2b-cart-edit-weight-skeleton]');
      var headerSkeleton = modal.querySelector('[data-b2b-cart-edit-weight-header-skeleton]');
      var headerReal = modal.querySelector('[data-b2b-cart-edit-weight-header-real]');
      var footerSkeleton = modal.querySelector('[data-b2b-cart-edit-weight-footer-skeleton]');
      var footerReal = modal.querySelector('[data-b2b-cart-edit-weight-footer-real]');
      var optionsWrap = modal.querySelector('[data-b2b-cart-edit-weight-options-wrap]');
      var empty = modal.querySelector('[data-b2b-cart-edit-weight-empty]');
      var error = modal.querySelector('[data-b2b-cart-edit-weight-error]');
      var status = modal.querySelector('[data-b2b-cart-edit-weight-status]');
      var conflictsSection = modal.querySelector('[data-b2b-cart-edit-weight-conflicts]');

      modal.classList.toggle('is-loading', isLoading);
      if (showFinalContent) modal.setAttribute('data-edit-weight-ready', '1');
      else modal.removeAttribute('data-edit-weight-ready');

      if (skeleton instanceof HTMLElement) skeleton.hidden = !isLoading;
      if (headerSkeleton instanceof HTMLElement) headerSkeleton.hidden = !isLoading;
      if (headerReal instanceof HTMLElement) headerReal.hidden = isLoading;
      if (footerSkeleton instanceof HTMLElement) footerSkeleton.hidden = !isLoading;
      if (footerReal instanceof HTMLElement) footerReal.hidden = isLoading;

      if (optionsWrap instanceof HTMLElement) optionsWrap.hidden = !isReady;
      if (empty instanceof HTMLElement) empty.hidden = !isEmpty;
      if (error instanceof HTMLElement) error.hidden = !isError;

      if (status instanceof HTMLElement) {
        status.textContent =
          isLoading
            ? 'Loading weight editor…'
            : isError
              ? 'Unable to load available weights.'
              : '';
      }

      if (showFinalContent) {
        renderEditConflictSection();
        updateEditSummary();
      } else if (conflictsSection instanceof HTMLElement) {
        conflictsSection.hidden = true;
      }

      var footerSummary = modal.querySelector('[data-b2b-cart-edit-weight-footer-real] .order-sheet-weight-modal__footer-summary');
      var footerNote = modal.querySelector('[data-b2b-cart-edit-weight-footer-note]');
      if (footerSummary instanceof HTMLElement) footerSummary.hidden = isError;
      if (footerNote instanceof HTMLElement && isError) footerNote.hidden = true;

      if (isLoading || isError) {
        var confirm = modal.querySelector('[data-b2b-cart-edit-weight-confirm]');
        if (confirm instanceof HTMLButtonElement) confirm.disabled = true;
      }
    }

    function updateEditSummary() {
      var modal = getEditModal();
      if (!(modal instanceof HTMLElement) || !editState) return;
      var selected = editState.selectedIds
        .map(function (id) {
          return editState.options.find(function (opt) {
            return String(opt.id) === String(id);
          });
        })
        .filter(Boolean);
      var stats = getEditResolutionStats();
      /** @type {Record<string, 1>} */
      var selectedWeightKeys = {};
      selected.forEach(function (opt) {
        var w = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.label);
        var key = w != null ? w.toFixed(3) : String(opt.label || opt.id || '');
        if (key) selectedWeightKeys[key] = 1;
      });
      var selectedWeightCount = Object.keys(selectedWeightKeys).length;
      var total = selected.reduce(function (sum, opt) {
        var w = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.label);
        return sum + (w != null ? w : 0);
      }, 0);
      var summary = modal.querySelector('[data-b2b-cart-edit-weight-summary]');
      var summaryQty = modal.querySelector('[data-b2b-cart-edit-weight-summary-qty]');
      var summaryTotal = modal.querySelector('[data-b2b-cart-edit-weight-summary-total]');
      var confirm = modal.querySelector('[data-b2b-cart-edit-weight-confirm]');
      var footerNote = modal.querySelector('[data-b2b-cart-edit-weight-footer-note]');
      if (summary) summary.textContent = String(selectedWeightCount);
      if (summaryQty) {
        summaryQty.textContent = stats.resolved + ' / ' + stats.original;
        summaryQty.classList.toggle('is-incomplete', stats.remaining > 0);
        summaryQty.classList.toggle('is-complete', stats.remaining === 0 && stats.original > 0);
      }
      if (summaryTotal) summaryTotal.textContent = total > 0 ? total.toFixed(3) + 'g' : '0.000g';
      updateConflictGuidanceMessage();
      if (footerNote instanceof HTMLElement) {
        footerNote.hidden = stats.remaining === 0 && stats.original > 0;
      }
      if (confirm instanceof HTMLButtonElement) {
        confirm.disabled =
          editBusy || stats.original < 1 || stats.remaining > 0;
      }
    }

    function renderEditOptions(filterText) {
      var modal = getEditModal();
      if (!(modal instanceof HTMLElement) || !editState) return;
      var list = modal.querySelector('[data-b2b-cart-edit-weight-options]');
      var emptySearch = modal.querySelector('[data-b2b-cart-edit-weight-empty-search]');
      if (!(list instanceof HTMLElement)) return;
      var q = String(filterText || '')
        .trim()
        .toLowerCase();

      /** @type {Record<string, { label: string, weight: number|null, options: Array<Record<string, unknown>>, selectedCount: number }>} */
      var groups = {};
      editState.options.forEach(function (opt) {
        var label = String(opt.label || opt.weight || '').trim();
        var weight = normalizeWeightGrams(opt.weight != null ? opt.weight : label);
        var key = weight != null ? weight.toFixed(3) : label;
        if (!groups[key]) {
          groups[key] = { label: label, weight: weight, options: [], selectedCount: 0 };
        }
        groups[key].options.push(opt);
        if (editState.selectedIds.indexOf(String(opt.id)) >= 0) groups[key].selectedCount += 1;
      });

      var rows = Object.keys(groups)
        .map(function (key) {
          return groups[key];
        })
        .filter(function (group) {
          if (!q) return true;
          return String(group.label).toLowerCase().indexOf(q) >= 0;
        })
        .sort(function (a, b) {
          var aSelected = a.selectedCount > 0 ? 1 : 0;
          var bSelected = b.selectedCount > 0 ? 1 : 0;
          if (aSelected !== bSelected) return bSelected - aSelected;
          return (a.weight || 0) - (b.weight || 0);
        });

      if (emptySearch instanceof HTMLElement) emptySearch.hidden = rows.length > 0 || !q;
      var stats = getEditResolutionStats();
      var replacementCap = stats.replacementCap;
      var replacementAdded = stats.replacement;
      list.innerHTML = rows
        .map(function (group, groupIndex) {
          var weightKey = group.weight != null ? group.weight.toFixed(3) : String(group.label || '');
          var originalBaseline = getOriginalBaselineCountForWeight(weightKey);
          var maxForGroup = group.options.length;
          var canDec = group.selectedCount > 0;
          var canInc =
            group.selectedCount < maxForGroup &&
            (group.selectedCount < originalBaseline || replacementAdded < replacementCap);
          var selectedClass =
            group.selectedCount > 0
              ? ' is-selected cart-sync-weight-editor__available-row--selected'
              : '';
          return (
            '<div class="order-sheet-weight-modal__checkbox-item cart-sync-weight-editor__available-row' +
            selectedClass +
            '" data-edit-weight-group="' +
            groupIndex +
            '">' +
            '<div class="order-sheet-weight-modal__weight-main">' +
            '<strong class="order-sheet-weight-modal__weight-value">' +
            escapeHtml(group.weight != null ? formatConflictWeight(group.weight) : group.label) +
            '</strong></div>' +
            '<div class="order-sheet-weight-modal__stock-meta cart-sync-weight-editor__available-qty">' +
            '<span class="order-sheet-weight-modal__stock-count cart-sync-weight-editor__available-qty-value">' +
            maxForGroup +
            '</span></div>' +
            '<div class="cart-sync-weight-editor__qty-stepper" role="group" aria-label="Selected quantity for ' +
            escapeHtml(group.label) +
            '">' +
            '<button type="button" class="cart-sync-weight-editor__qty-button" data-edit-weight-minus data-group-index="' +
            groupIndex +
            '"' +
            (!canDec ? ' disabled' : '') +
            ' aria-label="Decrease quantity">−</button>' +
            '<span class="cart-sync-weight-editor__qty-value">' +
            group.selectedCount +
            '</span>' +
            '<button type="button" class="cart-sync-weight-editor__qty-button" data-edit-weight-plus data-group-index="' +
            groupIndex +
            '"' +
            (!canInc ? ' disabled' : '') +
            ' aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<div class="cart-sync-weight-editor__available-action">' +
            '<button type="button" class="cart-sync-weight-editor__remove-btn" data-edit-weight-remove-group data-group-index="' +
            groupIndex +
            '"' +
            (group.selectedCount > 0 ? '' : ' disabled') +
            ' aria-label="Remove selected quantity for ' +
            escapeHtml(group.label) +
            '">' +
            '<span class="cart-sync-weight-editor__remove-icon" aria-hidden="true">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg></span>' +
            '<span>Remove</span>' +
            '</button></div>' +
            '</div>'
          );
        })
        .join('');

      /**
       * Drop one selected unit from a weight group (replacement extras first, then baseline).
       * @param {{ options: Array<Record<string, unknown>>, selectedCount: number }} group
       * @returns {boolean}
       */
      function decrementGroupSelection(group) {
        if (!editState || !group || group.selectedCount <= 0) return false;
        var removedId = '';
        for (var i = editState.selectedIds.length - 1; i >= 0; i--) {
          var id = editState.selectedIds[i];
          if (isBaselineOptionId(id)) continue;
          if (
            group.options.some(function (opt) {
              return String(opt.id) === String(id);
            })
          ) {
            removedId = String(id);
            editState.selectedIds.splice(i, 1);
            break;
          }
        }
        if (!removedId) {
          for (var j = editState.selectedIds.length - 1; j >= 0; j--) {
            var baselineId = editState.selectedIds[j];
            if (
              group.options.some(function (opt) {
                return String(opt.id) === String(baselineId);
              })
            ) {
              removedId = String(baselineId);
              editState.selectedIds.splice(j, 1);
              releaseBaselineOptionId(removedId);
              break;
            }
          }
        }
        return Boolean(removedId);
      }

      /**
       * Clear every selected unit for a weight group (same as repeated minus / Action Remove).
       * @param {{ options: Array<Record<string, unknown>>, selectedCount: number }} group
       */
      function clearGroupSelection(group) {
        if (!editState || !group) return;
        while (group.selectedCount > 0 && decrementGroupSelection(group)) {
          group.selectedCount -= 1;
        }
      }

      function refreshEditOptionsAfterChange() {
        renderEditOptions(
          modal.querySelector('[data-b2b-cart-edit-weight-search]') instanceof HTMLInputElement
            ? /** @type {HTMLInputElement} */ (modal.querySelector('[data-b2b-cart-edit-weight-search]')).value
            : ''
        );
        updateEditSummary();
      }

      list.querySelectorAll('[data-edit-weight-plus], [data-edit-weight-minus]').forEach(function (btn) {
        if (!(btn instanceof HTMLButtonElement)) return;
        btn.addEventListener('click', function () {
          if (!editState || editBusy) return;
          var idx = Number(btn.getAttribute('data-group-index'));
          var group = rows[idx];
          if (!group) return;
          var isPlus = btn.hasAttribute('data-edit-weight-plus');
          var weightKey = group.weight != null ? group.weight.toFixed(3) : String(group.label || '');
          var originalBaseline = getOriginalBaselineCountForWeight(weightKey);
          var liveStats = getEditResolutionStats();
          if (isPlus) {
            if (group.selectedCount >= group.options.length) return;
            if (
              group.selectedCount >= originalBaseline &&
              liveStats.replacement >= liveStats.replacementCap
            ) {
              return;
            }
            var nextOpt = group.options.find(function (opt) {
              return editState.selectedIds.indexOf(String(opt.id)) < 0;
            });
            if (!nextOpt) return;
            editState.selectedIds.push(String(nextOpt.id));
          } else {
            decrementGroupSelection(group);
          }
          refreshEditOptionsAfterChange();
        });
      });

      list.querySelectorAll('[data-edit-weight-remove-group]').forEach(function (btn) {
        if (!(btn instanceof HTMLButtonElement)) return;
        btn.addEventListener('click', function () {
          if (!editState || editBusy || btn.disabled) return;
          var idx = Number(btn.getAttribute('data-group-index'));
          var group = rows[idx];
          if (!group || group.selectedCount <= 0) return;
          clearGroupSelection(group);
          refreshEditOptionsAfterChange();
        });
      });
    }

    async function loadEditWeightOptions() {
      if (!editState) return;
      var wms = getWmsApi();
      var modal = getEditModal();
      if (!wms || !editState.productId || !editState.sku) {
        setEditFetchUi('error');
        return;
      }
      var runId = ++editState.requestSeq;
      setEditFetchUi('loading');
      try {
        if (typeof wms.ensureCartSerialsSynced === 'function') {
          await wms.ensureCartSerialsSynced(false).catch(function () {
            return null;
          });
        }
        if (typeof wms.ensureProductsLoaded === 'function') {
          await wms.ensureProductsLoaded([editState.productId]);
        }

        /** @type {Set<string>} */
        var keepSerialSet = new Set();
        (Array.isArray(editState.keepSerials) ? editState.keepSerials : []).forEach(function (serial) {
          var key = String(serial || '').trim();
          if (key) keepSerialSet.add(key);
        });

        // Include valid cart-held serials via keepSerials so they remain selectable.
        // Omit excludeLineKey so unavailable conflict serials stay locked out.
        var rawOptions =
          typeof wms.getAvailableOptions === 'function' ? wms.getAvailableOptions(editState.sku) : [];
        var filtered =
          typeof wms.filterAvailableOptions === 'function'
            ? wms.filterAvailableOptions(editState.sku, rawOptions, {
                keepSerials: keepSerialSet,
              })
            : rawOptions;

        if (!editState || runId !== editState.requestSeq) return;

        /** @type {Array<Record<string, unknown>>} */
        var mapped = (Array.isArray(filtered) ? filtered : [])
          .map(function (opt, index) {
            if (!opt || typeof opt !== 'object') return null;
            var serial = String(opt.serialNumber || opt.serial || '').trim();
            if (!serial) return null;
            var weight = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.value);
            var label =
              weight != null
                ? weight.toFixed(3) + ' g'
                : String(opt.label || opt.weight || '').trim();
            return {
              id: String(opt.id || serial || index),
              serialNumber: serial,
              weight: weight,
              label: label,
              isBaseline: keepSerialSet.has(serial),
              raw: opt,
            };
          })
          .filter(Boolean);

        /** @type {Record<string, 1>} */
        var seenSerials = {};
        mapped.forEach(function (opt) {
          var serial = String(opt.serialNumber || '').trim();
          if (serial) seenSerials[serial] = 1;
        });

        // Merge valid cart-selected units that are missing from live WMS availableOptions.
        var keepWeights = Array.isArray(editState.keepWeights) ? editState.keepWeights : [];
        var keepSerials = Array.isArray(editState.keepSerials) ? editState.keepSerials : [];
        keepWeights.forEach(function (weightLabel, index) {
          var serial = String(keepSerials[index] || '').trim();
          var weight = normalizeWeightGrams(weightLabel);
          if (serial && seenSerials[serial]) return;
          if (!serial) {
            var syntheticId = 'keep-noserial-' + index + '-' + (weight != null ? weight.toFixed(3) : index);
            mapped.push({
              id: syntheticId,
              serialNumber: '',
              weight: weight,
              label: weight != null ? weight.toFixed(3) + ' g' : String(weightLabel || '').trim(),
              isBaseline: true,
              raw: null,
            });
            return;
          }
          seenSerials[serial] = 1;
          mapped.push({
            id: 'keep-' + serial,
            serialNumber: serial,
            weight: weight,
            label: weight != null ? weight.toFixed(3) + ' g' : String(weightLabel || '').trim(),
            isBaseline: true,
            raw: null,
          });
        });

        editState.options = mapped.sort(function (a, b) {
          return (a.weight || 0) - (b.weight || 0);
        });

        /** @type {string[]} */
        var baselineIds = [];
        keepWeights.forEach(function (weightLabel, index) {
          var serial = String(keepSerials[index] || '').trim();
          var weight = normalizeWeightGrams(weightLabel);
          var match = null;
          if (serial) {
            match = editState.options.find(function (opt) {
              return (
                String(opt.serialNumber || '') === serial &&
                baselineIds.indexOf(String(opt.id)) < 0
              );
            });
          }
          if (!match) {
            match = editState.options.find(function (opt) {
              if (baselineIds.indexOf(String(opt.id)) >= 0) return false;
              if (!opt.isBaseline) return false;
              var optWeight = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.label);
              return weight != null && optWeight === weight;
            });
          }
          if (!match && weight != null) {
            match = editState.options.find(function (opt) {
              if (baselineIds.indexOf(String(opt.id)) >= 0) return false;
              var optWeight = normalizeWeightGrams(opt.weight != null ? opt.weight : opt.label);
              return optWeight === weight && String(opt.serialNumber || '') === serial;
            });
          }
          if (match) baselineIds.push(String(match.id));
        });

        editState.baselineSelectedIds = baselineIds.slice();
        editState.baselineCount = baselineIds.length;
        editState.selectedIds = baselineIds.slice();
        trimReplacementSelectionToCap();

        if (!editState.options.length) {
          setEditFetchUi('empty');
          updateEditSummary();
          return;
        }
        setEditFetchUi('ready');
        if (modal) {
          var search = modal.querySelector('[data-b2b-cart-edit-weight-search]');
          renderEditOptions(search instanceof HTMLInputElement ? search.value : '');
        }
        updateEditSummary();
      } catch (err) {
        if (typeof console !== 'undefined' && typeof console.warn === 'function') {
          console.warn('[b2b-cart-sync-availability] failed to load WMS weights', err && err.message ? err.message : err);
        }
        if (editState && runId === editState.requestSeq) setEditFetchUi('error');
      }
    }

    /**
     * @param {Record<string, unknown>} conflict
     * @param {{ skuGroup?: Record<string, unknown> }} [opts]
     */
    async function openEditWeightForConflict(conflict, opts) {
      var options = opts || {};
      var skuGroup = options.skuGroup || null;
      var modal = getEditModal();
      if (!(modal instanceof HTMLElement)) {
        deps.showCartFeedback(root, 'Weight editor is unavailable on this page.');
        return;
      }
      if (!getWmsApi()) {
        deps.showCartFeedback(root, 'Live weight availability is unavailable. Please try again.');
        return;
      }

      var cart = lastCart;
      if (!cart || !Array.isArray(cart.items)) {
        var refreshed = await deps.fetchJson(String(cfg.cartJsUrl || '/cart.js'));
        if (!refreshed.res.ok) {
          deps.showCartFeedback(root, 'Could not refresh cart before editing weight.');
          return;
        }
        cart = /** @type {Record<string, unknown>} */ (refreshed.data);
        lastCart = cart;
      }

      var sku = String(
        (skuGroup && (skuGroup.sku || skuGroup.key)) || conflict.sku || ''
      ).trim();
      var skuLines = sku ? findCartLinesForSku(cart, sku) : [];
      var line = null;
      if (conflict.lineKey) {
        line = (cart.items || []).find(function (item) {
          return item && String(item.key || '') === String(conflict.lineKey);
        });
      }
      if (!line && skuLines.length) line = skuLines[0];
      if (!line) {
        line = (cart.items || []).find(function (item) {
          if (!item) return false;
          if (
            conflict.sku &&
            String(itemSku(/** @type {Record<string, unknown>} */ (item))).toUpperCase() !==
              String(conflict.sku).toUpperCase()
          ) {
            return false;
          }
          return lineHasWeight(/** @type {Record<string, unknown>} */ (item), conflict.weight);
        });
      }
      if (!line) {
        deps.showCartFeedback(root, 'Could not find that cart line to edit.');
        return;
      }

      var lineItem = /** @type {Record<string, unknown>} */ (line);
      var productId =
        String((skuGroup && skuGroup.productId) || conflict.productId || '') ||
        (lineItem.product_id != null ? String(lineItem.product_id) : '');
      var resolvedSku = sku || itemSku(lineItem) || '';
      /** @type {string[]} */
      var extraLineKeys = [];
      if (skuLines.length) {
        skuLines.forEach(function (skuLine) {
          var key = String(skuLine.key || '');
          if (key && key !== String(lineItem.key || '')) extraLineKeys.push(key);
        });
      }

      var sourceConflicts =
        skuGroup && Array.isArray(skuGroup.conflicts) ? skuGroup.conflicts : [conflict];
      var replacementCtx = buildEditReplacementContext(
        skuLines.length ? skuLines : [lineItem],
        /** @type {Array<Record<string, unknown>>} */ (sourceConflicts)
      );
      var requiredReplacementQty = Math.max(
        0,
        Math.floor(Number(replacementCtx.originalConflictQty) || 0)
      );
      if (requiredReplacementQty < 1) requiredReplacementQty = 1;

      // Keep parent conflict modal open behind the child editor.
      // Independent modal state: child open/close must not destroy parent conflict state.
      // Child stacks above via higher z-index; Escape is owned by the open child.
      var availabilityModal = getAvailabilityModal();

      editState = {
        lineKey: String(lineItem.key || ''),
        productId: productId,
        sku: resolvedSku,
        targetQty: requiredReplacementQty,
        originalConflictQty: requiredReplacementQty,
        removedQty: 0,
        requiredReplacementQty: requiredReplacementQty,
        options: [],
        selectedIds: [],
        baselineSelectedIds: [],
        baselineCount: 0,
        baselineByWeight: Object.assign({}, replacementCtx.baselineByWeight || {}),
        originalBaselineByWeight: Object.assign({}, replacementCtx.baselineByWeight || {}),
        keepWeights: replacementCtx.keepWeights,
        keepSerials: replacementCtx.keepSerials,
        conflictRows: replacementCtx.conflictRows,
        requestSeq: 0,
        conflictId: String(conflict.id || ''),
        extraLineKeys: extraLineKeys,
        conflictWeights: replacementCtx.conflictWeights,
      };

      var title = modal.querySelector('[data-b2b-cart-edit-weight-title]');
      if (title) {
        title.textContent = String(
          (skuGroup && skuGroup.productName) || conflict.productName || itemTitle(lineItem) || 'Edit Weight'
        );
      }
      var subtitle = modal.querySelector('[data-b2b-cart-edit-weight-subtitle]');
      if (subtitle) {
        subtitle.textContent = 'Choose available weights to replace the unavailable selections below.';
      }
      var imageEl = modal.querySelector('[data-b2b-cart-edit-weight-image]');
      if (imageEl instanceof HTMLImageElement) {
        var src = String((skuGroup && skuGroup.image) || conflict.image || itemImage(lineItem) || '');
        if (src) {
          imageEl.src = src;
          imageEl.classList.remove('order-sheet-weight-modal__product-image--placeholder');
        }
      }
      var search = modal.querySelector('[data-b2b-cart-edit-weight-search]');
      if (search instanceof HTMLInputElement) search.value = '';

      // Skeleton only until WMS merge + baseline init finish (no partial final UI).
      setEditFetchUi('loading');

      var editBtn = availabilityModal && availabilityModal.querySelector(
        '[data-b2b-sync-availability-edit-sku][data-sku-key="' + String((skuGroup && skuGroup.key) || '').replace(/"/g, '') + '"]'
      );
      if (editBtn instanceof HTMLButtonElement) {
        editBtn.disabled = true;
        editBtn.setAttribute('aria-busy', 'true');
      }

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('order-sheet-weight-modal-open');
      try {
        await loadEditWeightOptions();
      } finally {
        if (editBtn instanceof HTMLButtonElement) {
          editBtn.disabled = false;
          editBtn.removeAttribute('aria-busy');
        }
      }
    }

    /** @param {Record<string, unknown>} skuGroup */
    async function openEditWeightForSku(skuGroup) {
      if (!skuGroup || !Array.isArray(skuGroup.conflicts) || !skuGroup.conflicts.length) return;
      expandedSkuKey = String(skuGroup.key || '');
      await openEditWeightForConflict(
        /** @type {Record<string, unknown>} */ (skuGroup.conflicts[0]),
        { skuGroup: skuGroup }
      );
    }

    async function confirmEditWeight() {
      if (!editState || editBusy) return;
      var stats = getEditResolutionStats();
      if (stats.original < 1 || stats.remaining > 0) {
        deps.showCartFeedback(
          root,
          stats.remaining === 1
            ? 'Replace or remove 1 more unavailable item before updating.'
            : 'Replace or remove ' + stats.remaining + ' more unavailable items before updating.'
        );
        return;
      }
      if (stats.replacement !== stats.replacementCap) {
        deps.showCartFeedback(
          root,
          'Replacement quantity does not match the remaining unavailable items.'
        );
        return;
      }

      var selected = editState.selectedIds
        .map(function (id) {
          return editState.options.find(function (opt) {
            return String(opt.id) === String(id);
          });
        })
        .filter(Boolean);
      // selected may be only baseline, only replacements, or a mix after editing cart weights.
      if (selected.length !== editState.selectedIds.length) return;

      var weights = selected.map(function (opt) {
        return opt.weight != null ? Number(opt.weight).toFixed(3) : String(opt.label || '').trim();
      });
      var serials = selected.map(function (opt) {
        return String(opt.serialNumber || '').trim();
      });
      if (
        serials.some(function (s) {
          return !s;
        })
      ) {
        deps.showCartFeedback(root, 'Selected weights are missing serial numbers. Please retry.');
        return;
      }

      editBusy = true;
      updateEditSummary();
      try {
        /** @type {Record<string, 1>} */
        var previousKeys = {};
        if (lastCart && Array.isArray(lastCart.items)) {
          lastCart.items.forEach(function (item) {
            if (item && item.key != null) previousKeys[String(item.key)] = 1;
          });
        }
        var cart = lastCart;
        var line =
          cart && Array.isArray(cart.items)
            ? cart.items.find(function (item) {
                return item && String(item.key || '') === String(editState.lineKey);
              })
            : null;
        if (!line) {
          deps.showCartFeedback(root, 'Cart line is no longer available.');
          closeEditWeightModal({ reopenAvailability: true });
          return;
        }
        var props =
          line.properties && typeof line.properties === 'object'
            ? Object.assign({}, /** @type {Record<string, unknown>} */ (line.properties))
            : {};

        var nextQuantity = weights.length;
        if (nextQuantity > 0) {
          props.Weight = weights.join(', ');
          if (serials.filter(Boolean).length) props.SerialNumbers = serials.join(', ');
          else delete props.SerialNumbers;
        }

        var changeBody =
          nextQuantity > 0
            ? {
                id: String(editState.lineKey),
                quantity: nextQuantity,
                properties: props,
              }
            : {
                id: String(editState.lineKey),
                quantity: 0,
              };
        var change = await deps.fetchJson(String(cfg.cartChangeJsUrl || '/cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(changeBody),
        });
        var changedCart = /** @type {Record<string, unknown>} */ (change.data);
        if (!change.res.ok || changedCart.errors) {
          var err =
            (typeof changedCart.message === 'string' && changedCart.message) ||
            (typeof changedCart.errors === 'string' && changedCart.errors) ||
            'Could not update selected weights.';
          deps.showCartFeedback(root, err);
          return;
        }

        if (Array.isArray(editState.extraLineKeys) && editState.extraLineKeys.length) {
          /** @type {Record<string, number>} */
          var extras = {};
          editState.extraLineKeys.forEach(function (key) {
            if (key) extras[String(key)] = 0;
          });
          if (Object.keys(extras).length) {
            if (typeof deps.updateCartByKeys === 'function') {
              var cleared = await deps.updateCartByKeys(extras, { rootBusy: false });
              if (cleared) changedCart = /** @type {Record<string, unknown>} */ (cleared);
            } else {
              var updateUrl = String(cfg.cartUpdateJsUrl || '/cart/update.js');
              var clearResult = await deps.fetchJson(updateUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: extras }),
              });
              if (clearResult.res.ok && !clearResult.data.errors) {
                changedCart = /** @type {Record<string, unknown>} */ (clearResult.data);
              }
            }
          }
        }

        var wms = getWmsApi();
        if (wms && typeof wms.handleSuccessfulCartMutation === 'function') {
          try {
            wms.handleSuccessfulCartMutation(changedCart);
          } catch (_) {}
        }

        afterCartMutation(changedCart, 'b2b-cart-review:availability-edit-weight', {
          previousKeys: previousKeys,
        });
        closeEditWeightModal({ reopenAvailability: conflicts.length > 0 });
      } catch (_) {
        deps.showCartFeedback(root, 'Network error while updating weights.');
      } finally {
        editBusy = false;
        updateEditSummary();
      }
    }

    function bindUi() {
      var availabilityModal = getAvailabilityModal();
      if (availabilityModal instanceof HTMLElement && availabilityModal.dataset.b2bSyncAvailabilityBound !== '1') {
        availabilityModal.dataset.b2bSyncAvailabilityBound = '1';
        availabilityModal.addEventListener('click', function (e) {
          var t = e.target;
          if (!(t instanceof Element)) return;
          var editModalOpen = (function () {
            var editModal = getEditModal();
            return editModal instanceof HTMLElement && !editModal.hidden;
          })();
          if (t.closest('[data-b2b-sync-availability-dismiss]')) {
            if (editModalOpen) return;
            if (availabilityModal.getAttribute('data-overlay-dismiss') === 'false') return;
            closeAvailabilityModal();
            return;
          }
          if (t.closest('[data-b2b-sync-availability-close], [data-b2b-sync-availability-cancel]')) {
            if (editModalOpen) return;
            closeAvailabilityModal();
            return;
          }

          var toggle = t.closest('[data-b2b-sync-availability-toggle]');
          if (toggle instanceof HTMLElement) {
            var toggleKey = String(toggle.getAttribute('data-sku-key') || '');
            if (!toggleKey) return;
            expandedSkuKey = String(expandedSkuKey) === toggleKey ? '' : toggleKey;
            renderConflicts();
            return;
          }

          var moreBtn = t.closest('[data-b2b-sync-availability-more]');
          if (moreBtn instanceof HTMLElement) {
            var moreKey = String(moreBtn.getAttribute('data-sku-key') || '');
            if (!moreKey) return;
            weightsExpandedBySku[moreKey] = !weightsExpandedBySku[moreKey];
            renderConflicts();
            return;
          }

          var editSkuBtn = t.closest('[data-b2b-sync-availability-edit-sku]');
          if (editSkuBtn instanceof HTMLButtonElement) {
            var editKey = String(editSkuBtn.getAttribute('data-sku-key') || '');
            var editGroup = findSkuGroup(editKey);
            if (editGroup) openEditWeightForSku(editGroup);
            return;
          }

          var removeSkuBtn = t.closest('[data-b2b-sync-availability-remove-sku]');
          if (removeSkuBtn instanceof HTMLButtonElement) {
            var removeKey = String(removeSkuBtn.getAttribute('data-sku-key') || '');
            var removeGroup = findSkuGroup(removeKey);
            if (removeGroup) removeEntireSku(removeGroup, removeSkuBtn);
            return;
          }

          if (t.closest('[data-b2b-sync-availability-continue]')) {
            var unresolved = getSkuGroups();
            if (!unresolved.length) {
              closeAvailabilityModal();
              return;
            }
            openEditWeightForSku(unresolved[0]);
          }
        });
      }

      var editModal = getEditModal();
      if (editModal instanceof HTMLElement && editModal.dataset.b2bEditWeightBound !== '1') {
        editModal.dataset.b2bEditWeightBound = '1';
        editModal.addEventListener('click', function (e) {
          var t = e.target;
          if (!(t instanceof Element)) return;
          if (t.closest('[data-b2b-cart-edit-weight-close]')) {
            closeEditWeightModal({ reopenAvailability: true });
            return;
          }
          if (t.closest('[data-b2b-cart-edit-weight-retry]')) {
            loadEditWeightOptions();
            return;
          }
          var removeConflictBtn = t.closest('[data-b2b-cart-edit-weight-remove-conflict]');
          if (removeConflictBtn instanceof HTMLButtonElement) {
            var conflictId = String(removeConflictBtn.getAttribute('data-conflict-id') || '');
            removeConflictRowFromEditor(conflictId, removeConflictBtn);
            return;
          }
          if (t.closest('[data-b2b-cart-edit-weight-confirm]')) {
            confirmEditWeight();
          }
        });
        // Escape closes only the child editor; parent conflict modal remains.
        document.addEventListener(
          'keydown',
          function (e) {
            if (e.key !== 'Escape') return;
            if (!(editModal instanceof HTMLElement) || editModal.hidden) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            closeEditWeightModal({ reopenAvailability: true });
          },
          true
        );
        var search = editModal.querySelector('[data-b2b-cart-edit-weight-search]');
        if (search instanceof HTMLInputElement) {
          search.addEventListener('input', function () {
            renderEditOptions(search.value);
          });
        }
      }
    }

    bindUi();

    return {
      /**
       * @param {Record<string, unknown>} data
       * @param {Record<string, unknown>} cart
       * @returns {boolean} true when handled as availability modal
       */
      handleSyncError: function (data, cart) {
        var extracted = extractAvailabilityConflicts(data);
        if (!extracted || !extracted.length) return false;
        var enriched = enrichConflictsFromCart(extracted, cart || {});
        if (!enriched.length) return false;
        return openAvailabilityModal(enriched, cart || {});
      },
      close: closeAvailabilityModal,
      reset: function () {
        closeEditWeightModal({ reopenAvailability: false });
        closeAvailabilityModal();
      },
      hasOpenConflicts: function () {
        return conflicts.length > 0;
      },
    };
  }

  win.B2bCartSyncAvailability = {
    createController: createController,
    extractAvailabilityConflicts: extractAvailabilityConflicts,
    isAvailabilityConflictCode: isAvailabilityConflictCode,
    enrichConflictsFromCart: enrichConflictsFromCart,
    pruneConflictsAgainstCart: pruneConflictsAgainstCart,
    groupConflictsBySku: groupConflictsBySku,
  };
})(window);
